/* eslint-disable no-nested-ternary */
/**
 * @name Report
 * @description Componente común para mostrar reportes
 * @author Ricardo Guerra
 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import i18n from 'i18next';
import styled from 'styled-components';

import ReactDataGrid from '@inovua/reactdatagrid-community';
import '@inovua/reactdatagrid-community/index.css';

import { LoadingWrapper, NoRegistersFound } from '@components';
import { Flex, MUI, Unicons, Typography } from '@styles';
import { Helpers } from '@helpers';

import _ from 'lodash';

// Components
import MenuColumns from './components/menu-columns';
import MenuExport from './components/menu-export';

class Report extends Component {
  constructor(props) {
    super(props);
    this.state = {
      hiddenColumns: [],
      visibleColumns: [],
      // orderColumns: [],
      rows: [],
      searchText: '',
      gridRef: null,
      showHeader: props.showHeader !== false,
      showSearchBox: props.showSearchBox ? props.showSearchBox : false,
    };
  }

  componentDidMount() {
    this.setColumns();
    this.setRows();
  }

  componentDidUpdate(prevProps) {
    if (!_.isEqual(this.props.columns, prevProps.columns)) this.setColumns();
    if (!_.isEqual(this.props.rows, prevProps.rows)) this.setRows();
  }

  setGridRef = (ref) => {
    this.setState({ gridRef: ref });
  }

  setColumns = () => {
    const columns = Array.isArray(this.props.columns) ? this.props.columns : [];
    const visibleColumns = columns.filter((column) => column.visible).map((column) => ({ ...column }));
    const hiddenColumns = visibleColumns.filter((column) => !column.hidden);
    this.setState({ visibleColumns, hiddenColumns });
  }

  setRows = () => {
    const rows = Array.isArray(this.props.rows) ? this.props.rows : [];
    const { searchText } = this.state;
    const currentColumns = this.getCurrentColumns();
    const filteredRows = this.applySearchFilter(rows, searchText, currentColumns);
    this.setState({ rows: filteredRows });
  }

  // *************************************
  // VISIBLE COLUMNS
  // *************************************

  hideColumnHandler = (item) => {
    if (!item || !item.name) {
      return;
    }

    const visibleColumns = this.state.visibleColumns.map((column) => {
      if (column.name === item.name) {
        return { ...column, hidden: !item.hidden };
      }
      return column;
    });
    const hiddenColumns = visibleColumns.filter((column) => !column.hidden);

    this.setState({ visibleColumns, hiddenColumns });
  }

  // *************************************
  // SEARCH BOX
  // *************************************

  getCurrentColumns = () => {
    const { gridRef, hiddenColumns } = this.state;
    return gridRef && gridRef.current && Array.isArray(gridRef.current.columns) ? gridRef.current.columns : hiddenColumns;
  };

  applySearchFilter = (rows, searchText, columns) => {
    const safeRows = Array.isArray(rows) ? rows : [];
    const safeColumns = Array.isArray(columns) ? columns : [];
    const normalizedSearch = Helpers.removeAccents(`${searchText || ''}`).toLowerCase().trim();
    if (!normalizedSearch || safeColumns.length === 0) {
      return safeRows;
    }

    return safeRows.filter((row) => (
      safeColumns.reduce((acc, col) => {
        const value = Helpers.removeAccents(`${row[col.name]}`).toLowerCase();
        return acc || value.indexOf(normalizedSearch) !== -1;
      }, false)
    ));
  };

  onSearchChange = ({ target: { value } }) => {
    const currentColumns = this.getCurrentColumns();
    const newData = this.applySearchFilter(this.props.rows, value, currentColumns);
    this.setState({ searchText: value, rows: newData });
  };

  clearSearch = () => {
    const currentColumns = this.getCurrentColumns();
    const newData = this.applySearchFilter(this.props.rows, '', currentColumns);
    this.setState({ searchText: '', rows: newData });
  };

  getEmptyState = () => {
    const { searchText } = this.state;
    const normalizedSearch = `${searchText || ''}`.trim();

    if (!normalizedSearch) {
      return <NoRegistersFound />;
    }

    return (
      <Styled.EmptyStateWrapper>
        <NoRegistersFound direction="column" />
        <Typography.Text color="greyDarkAlpha">
          {`${i18n.t('GenericMessages.search')}: "${normalizedSearch}"`}
        </Typography.Text>
      </Styled.EmptyStateWrapper>
    );
  };

  render() {
    const { rowsLength, rows, styles } = this.props;
    const totalRows = Number.isInteger(rowsLength) ? rowsLength : (Array.isArray(rows) ? rows.length : 0);

    return (
      <LoadingWrapper area="loading-report">
        {(this.state.showHeader) && (
          <Styled.ReportHeader>
            <Flex.Row flex="1" padding="4px 0">
              {/* Registros totales */}
              <Typography.Text>
                {`${i18n.t('GenericMessages.total')}: `}
                <Typography.Medium>
                  {`${totalRows}`}
                  <Typography.Span color="greyDarkAlpha">{` - ${i18n.t('GenericMessages.registers')}`}</Typography.Span>
                </Typography.Medium>
              </Typography.Text>
              <MUI.Divider orientation="vertical" margin="0 16px" style={{ height: '20px' }} />
              {/* Registros filtrados */}
              <Typography.Text>
                {`${i18n.t('GenericMessages.filtered')}: `}
                <Typography.Medium>
                  {`${this.state.rows.length}`}
                  <Typography.Span color="greyDarkAlpha">{` - ${i18n.t('GenericMessages.registers')}`}</Typography.Span>
                </Typography.Medium>
              </Typography.Text>
            </Flex.Row>
            {/* Buscador */}
            {(this.state.showSearchBox) && (
              <MUI.Box padding="0 8px" marginRight="8px">
                <Styled.SearchTextField
                  type="text"
                  placeholder={`${i18n.t('GenericMessages.search')}...`}
                  value={this.state.searchText}
                  onChange={this.onSearchChange}
                  InputProps={{
                    startAdornment: <Unicons.Search size="20" style={{ minWidth: '20px', minHeight: '20px' }} />,
                    endAdornment: this.state.searchText ? (
                      <MUI.Tooltip title={i18n.t('GenericMessages.clear')}>
                        <Styled.ClearButton type="button" onClick={this.clearSearch}>
                          <Unicons.Close size="16" />
                        </Styled.ClearButton>
                      </MUI.Tooltip>
                    ) : null,
                  }}
                />
              </MUI.Box>
            )}
            {/* Menú de exportar */}
            {(this.props.exportToCSV || this.props.exportAllToExcel || this.props.exportFiltersToExcel) && (
              <MUI.Box marginRight="8px">
                <MenuExport
                  columns={this.state.visibleColumns}
                  rows={this.state.rows}
                  exportToCSV={this.props.exportToCSV}
                  exportAllToExcel={this.props.exportAllToExcel}
                  exportFiltersToExcel={this.props.exportFiltersToExcel}
                  exportFileName={this.props.exportFileName}
                  onExportAllToExcel={this.props.onExportAllToExcel || (() => null)}
                  onExportFiltersToExcel={this.props.onExportFiltersToExcel || (() => null)}
                />
              </MUI.Box>
            )}
            {/* Menú de columnas */}
            <MUI.Box>
              <MenuColumns
                columns={this.state.visibleColumns}
                onHideColumn={this.hideColumnHandler}
              />
            </MUI.Box>
          </Styled.ReportHeader>
        )}
        <Styled.ReportGrid>
          <ReactDataGrid
            idProperty="id"
            handle={this.setGridRef}
            columns={this.state.hiddenColumns}
            dataSource={this.state.rows}
            showCellBorders={'vertical'}
            showColumnMenuGroupOptions={false}
            showColumnMenuLockOptions={false}
            showColumnMenuSortOptions={false}
            showColumnMenuTool={false}
            shareSpaceOnResize={false}
            resizable={true}
            reorderColumns={true}
            scrollProps={{
              autoHide: false,
              alwaysShowTrack: true,
              scrollThumbWidth: 8,
              scrollThumbOverWidth: 8
            }}
            emptyText={this.getEmptyState()}
            style={styles}
          />
        </Styled.ReportGrid>
      </LoadingWrapper>
    );
  }
}

Report.propTypes = {
  // Colección de columnas a mostrar en el grid. 
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string.isRequired,
      header: PropTypes.string.isRequired,
      visible: PropTypes.bool.isRequired,
      hidden: PropTypes.bool,
      enableHiding: PropTypes.bool,
      resizable: PropTypes.bool,
      sortable: PropTypes.bool,
      draggable: PropTypes.bool,
      minWidth: PropTypes.number,
    })
  ),
  // Registros mostrados actualmente en el grid.
  rows: PropTypes.array.isRequired,
  // Número de registros mostrados actualmente en el grid.
  rowsLength: PropTypes.number.isRequired,
  // Boolean que muestra el header del componente.
  showHeader: PropTypes.bool,
  // Boolean que muestra el input de busqueda.
  showSearchBox: PropTypes.bool,
  // Boolean que muestra la opción de exportar a csv.
  exportToCSV: PropTypes.bool,
  // Boolean que muestra la opción de exportar a todos los registros a Excel.
  exportAllToExcel: PropTypes.bool,
  // Boolean que muestra la opción de exportar a los registros filtrados a Excel.
  exportFiltersToExcel: PropTypes.bool,
  // Nombre que se le da al archivo cuando se exportan los registros.
  exportFileName: PropTypes.string,
  // Función para exportar a todos los registros a Excel.
  onExportAllToExcel: PropTypes.func,
  // Función para exportar a los registros filtrados a Excel.
  onExportFiltersToExcel: PropTypes.func,
  // Styles
  styles: PropTypes.object
};

// *************************************
// STYLED COMPONENTS                               
// *************************************

const Styled = {};

Styled.ReportGrid = styled.div`
  .InovuaReactDataGrid--theme-default-light {
    color: ${(props) => props.theme.colors.greyDark};
    font-size: ${(props) => props.theme.typography.bodyText};
    font-variant: tabular-nums;
    font-weight: normal;
    line-height: 1.3;
    letter-spacing: 0.0175em;
  }

  .InovuaReactDataGrid--theme-default-light .InovuaReactDataGrid__column-header__content {
    font-family: 'Bryant Pro Medium';
    font-weight: normal;
    line-height: 1.3;
    letter-spacing: 0.0175em;
  }
`;

Styled.ReportHeader = styled(Flex.Row)`
  padding: 8px 0px;
`;

Styled.SearchTextField = styled(MUI.TextFieldIcon)`
  &.MuiFormControl-root {
    min-height: 40px;
  }
`;

Styled.ClearButton = styled.button`
  border: none;
  background: transparent;
  padding: 0;
  margin: 0;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${(props) => props.theme.colors.greyDarkAlpha};
  cursor: pointer;

  &:hover {
    color: ${(props) => props.theme.colors.greyDark};
  }
`;

Styled.EmptyStateWrapper = styled(Flex.Col)`
  align-items: center;
  justify-content: center;
  gap: 4px;
`;

export default Report;
